/**
 * Thin Sentry wrapper, gated on NEXT_PUBLIC_SENTRY_DSN.
 *
 * When the DSN is absent (local dev, CI without secrets, etc.) every call is a
 * no-op so the rest of the app never has to branch on "is Sentry wired?".
 *
 * When the DSN is present the real @sentry/nextjs SDK is used.  The SDK
 * package must be listed as a dependency and the sentry.client.config.ts /
 * sentry.server.config.ts entry-points must initialise it — this file is
 * intentionally init-free to avoid double-init.
 */

type CaptureContext = Record<string, unknown>;

/**
 * Returns the in-flight forwarding promise so callers (and tests) can await
 * it deterministically. Callers may safely ignore it — the existing
 * fire-and-forget usage in app/error.tsx is unaffected (it never awaits, and
 * the promise never rejects: load/forward failures are swallowed and logged).
 */
export function captureException(
  err: unknown,
  ctx?: CaptureContext,
): Promise<void> {
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

  if (!dsn) {
    // eslint-disable-next-line no-console
    console.error("[observability] Sentry DSN not configured — error not forwarded:", err, ctx);
    return Promise.resolve();
  }

  // Dynamic import keeps the SDK out of the bundle when DSN is absent.
  // At runtime (DSN present) the module is already loaded by the Sentry
  // config entry-points, so this resolves synchronously from the module
  // cache in practice.
  return import("@sentry/nextjs")
    .then(({ captureException: sentryCaptureException }) => {
      sentryCaptureException(err, ctx ? { extra: ctx } : undefined);
    })
    .catch((importErr) => {
      // eslint-disable-next-line no-console
      console.error("[observability] Failed to load @sentry/nextjs:", importErr);
    });
}
